import React, { useState, useEffect } from 'react';
import { Mail, Settings, Trash2 } from 'lucide-react';
import EmailTemplateManager from './EmailTemplateManager';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const apiService = {
  get: (url, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.get(`${API_BASE_URL}${url}`, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  },
  post: (url, data, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.post(`${API_BASE_URL}${url}`, data, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  },
  delete: (url, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.delete(`${API_BASE_URL}${url}`, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  }
};

const TrainingEmailPanel = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const tabContent = () => {
    switch (currentTab) {
      case 0:
        return <EmailAssignmentManager />;
      case 1:
        return <EmailTemplateManager />;
      default:
        return <EmailAssignmentManager />;
    }
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-xl shadow-lg overflow-hidden border border-steel-200">
      {/* Header with statistics */}
      <div className="px-6 py-4 bg-gradient-to-r from-brand-50 to-steel-50 border-b border-steel-200">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-900">
            Email Notification System
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-steel-200 bg-steel-50">
        <nav className="-mb-px flex space-x-8 px-6">
          <button
            onClick={() => setCurrentTab(0)}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              currentTab === 0
                ? 'border-brand-500 text-brand-500'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
            }`}
          >
            <Settings className="h-4 w-4 mr-2" />
            Training Assignments
          </button>
          <button
            onClick={() => setCurrentTab(1)}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              currentTab === 1
                ? 'border-brand-500 text-brand-500'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
            }`}
          >
            <Mail className="h-4 w-4 mr-2" />
            Email Templates
          </button>
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto">
        {tabContent()}
      </div>

    </div>
  );
};

// Component for managing template assignments to trainings
const EmailAssignmentManager = () => {
  const [assignments, setAssignments] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    training_id: '',
    email_template_id: '',
    template_type: 'participants_invitation'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assignmentsRes, trainingsRes, templatesRes] = await Promise.all([
        apiService.get('/training-email-assignments/'),
        apiService.get('/training-center-qualifications/'),
        apiService.get('/email-templates/')
      ]);

      setAssignments(assignmentsRes.data.results || assignmentsRes.data);
      setTrainings(trainingsRes.data.results || trainingsRes.data);
      setTemplates(templatesRes.data.results || templatesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async (trainingId, templateId, templateType) => {
    try {
      await apiService.post('/training-email-assignments/', {
        training_id: trainingId,
        email_template_id: templateId,
        template_type: templateType,
        is_active: true
      });
      fetchData();
    } catch (error) {
      console.error('Error creating assignment:', error);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      try {
        await apiService.delete(`/training-email-assignments/${assignmentId}/`);
        fetchData();
      } catch (error) {
        console.error('Error deleting assignment:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold text-slate-900 mb-4">
        Template Training Assignments
      </h2>
      
      <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6">
        <div className="text-sm text-brand-700">
          This section allows you to assign specific email templates to individual trainings. 
          This way each training can have its own customized notifications.
        </div>
      </div>

      {assignments.length === 0 ? (
        <div className="bg-white border border-steel-200 rounded-xl">
          <div className="text-center py-12">
            <div className="text-slate-500 text-lg mb-2">No assignments found</div>
            <div className="text-slate-400 text-sm mb-4">
              Start by creating email templates, then assign them to specific trainings.
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="bg-brand-500 text-white px-4 py-2 rounded-xl hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/20"
            >
              Add First Assignment
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-slate-600">
              {assignments.length} assignment{assignments.length !== 1 ? 's' : ''} found
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="bg-brand-500 text-white px-4 py-2 rounded-xl hover:bg-brand-600 transition-colors flex items-center shadow-lg shadow-brand-500/20"
            >
              <Settings className="h-4 w-4 mr-2" />
              Add Assignment
            </button>
          </div>
          {assignments.map((assignment) => (
            <div key={assignment.id} className="bg-white border border-steel-200 rounded-xl p-6 hover:bg-brand-50 hover:shadow-md transition-all">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    {assignment.training?.qualification_type?.qualification_type_data?.name || assignment.training?.qualification_type?.name || 'Unknown Qualification'}
                  </h3>
                  <div className="text-sm text-slate-500 mb-3">
                    <strong>Training Center:</strong> {assignment.training?.training_center?.name}
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm">
                      <strong>Email Template:</strong> {assignment.email_template?.name}
                    </div>
                    <div className="text-sm">
                      <strong>Template Type:</strong> {assignment.template_type_display}
                    </div>
                    <div className="text-sm">
                      <strong>Status:</strong> 
                      <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                        assignment.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {assignment.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <button
                    onClick={() => handleDeleteAssignment(assignment.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Delete assignment"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Assignment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-steel-200">
              <h3 className="text-lg font-medium text-slate-900">
                Create New Assignment
              </h3>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Training
                  </label>
                  <select
                    value={formData.training_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, training_id: e.target.value }))}
                    className="w-full border border-steel-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">Select training</option>
                    {trainings
                      .sort((a, b) => {
                        const aName = `${a.training_center?.name} - ${a.qualification_type?.qualification_type_data?.name || a.qualification_type?.name || 'Unknown'}`;
                        const bName = `${b.training_center?.name} - ${b.qualification_type?.qualification_type_data?.name || b.qualification_type?.name || 'Unknown'}`;
                        return aName.localeCompare(bName);
                      })
                      .map(training => (
                        <option key={training.id} value={training.id}>
                          {training.training_center?.name} - {training.qualification_type?.qualification_type_data?.name || training.qualification_type?.name || 'Unknown Qualification'}
                        </option>
                      ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email Template
                  </label>
                  <select
                    value={formData.email_template_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_template_id: e.target.value }))}
                    className="w-full border border-steel-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">Select template</option>
                    {templates.filter(t => t.is_active).map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Template Type
                  </label>
                  <select
                    value={formData.template_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, template_type: e.target.value }))}
                    className="w-full border border-steel-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <optgroup label="Uczestnicy szkolenia">
                      <option value="participants_invitation">Zaproszenie na szkolenie</option>
                      <option value="participants_cancellation">Anulowanie szkolenia</option>
                      <option value="participants_reminder">Przypomnienie o szkoleniu</option>
                    </optgroup>
                    <optgroup label="Przełożeni">
                      <option value="supervisors_invitation">Zaproszenie na szkolenie</option>
                      <option value="supervisors_cancellation">Anulowanie szkolenia</option>
                    </optgroup>
                    <optgroup label="Centrum szkoleniowe">
                      <option value="training_center_confirmation">Potwierdzenie uczestnictwa</option>
                    </optgroup>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-steel-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setFormData({ training_id: '', email_template_id: '', template_type: 'participants_invitation' });
                }}
                className="px-4 py-2 border border-steel-300 rounded-xl text-slate-700 hover:bg-steel-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (formData.training_id && formData.email_template_id) {
                    await handleCreateAssignment(formData.training_id, formData.email_template_id, formData.template_type);
                    setShowModal(false);
                    setFormData({ training_id: '', email_template_id: '', template_type: 'participants_invitation' });
                  }
                }}
                disabled={!formData.training_id || !formData.email_template_id}
                className="px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 disabled:bg-steel-400 disabled:cursor-not-allowed transition-colors shadow-lg shadow-brand-500/20"
              >
                Create Assignment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingEmailPanel;